/**
 * basketball-shoot.js — GDD 6.4.1.
 *
 * Three hoops and three balls. Near 100, mid 250, and a far hoop worth 1000
 * that only pays on a SWISH — touch its rim on the way through and the shot is
 * worth nothing. Rims are 90% of regulation (41.1 cm), which is what makes an
 * ordinary-looking shot rattle out.
 *
 * The hoops are staggered sideways as well as in depth. Three rims in a single
 * file would leave the far one permanently screened by the near one, so each
 * gets its own lane from the counter.
 *
 * Two mechanics carry the stall:
 *
 *   - **Scoring is a plane crossing, not a trigger volume.** Each frame the
 *     ball's path is checked against the rim plane; a DOWNWARD crossing inside
 *     the rim radius is a basket. The crossing point is interpolated between
 *     frames, so a fast ball cannot skip over the plane between steps.
 *   - **Spin bends the bounce** (GDD 6.4.1, 0.3x influence). On rim contact the
 *     ball gets an impulse opposing the surface velocity its own spin produces
 *     at the contact point. Backspin therefore drags the ball down into the
 *     hoop — the shooter's roll — and topspin kicks it out.
 *
 * @module stalls/basketball-shoot
 */

import * as THREE from 'three';
import { BASKETBALL_SHOOT, PALETTE, HUB, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { bus } from '../core/events.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';

const CFG = BASKETBALL_SHOOT;

/** One rim shell, shared by all three hoops. */
let RIM_SHELL = null;

/**
 * A torus trimesh laid flat, for the rim. A ball can rattle around inside a
 * trimesh ring; no primitive collider gives a hole to shoot through.
 * @returns {{ vertices: Float32Array, indices: Uint32Array }}
 */
function rimShell() {
  if (RIM_SHELL) return RIM_SHELL;
  const g = new THREE.TorusGeometry(CFG.rimRadius, CFG.rimTube, 6, 20);
  g.rotateX(Math.PI / 2);
  RIM_SHELL = {
    vertices: new Float32Array(g.attributes.position.array),
    indices: new Uint32Array(g.index.array),
  };
  g.dispose();
  return RIM_SHELL;
}

class BasketballShoot extends StallBase {
  constructor(ctx) {
    super(ctx);
    /**
     * @type {Array<{ key: string, def: object, local: THREE.Vector3, world: THREE.Vector3,
     *                group: THREE.Object3D, rim: object, backboard: object,
     *                throughs: number, baskets: number }>}
     */
    this.hoops = [];
    /**
     * @type {Array<{ handle: object, prev: THREE.Vector3|null, rimTouched: Set<string>,
     *                restTimer: number, age: number, done: boolean, resolved: boolean,
     *                result: string|null }>}
     */
    this.balls = [];
    this._settleTimer = 0;
    this._offCollision = null;
    this._scratch = new THREE.Vector3();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._buildCourt();
    for (const def of CFG.hoops) this._buildHoop(def);

    this.buildTray({ width: 0.86, y: 1.14, z: 0.74 });
    // On the side wall, angled in. Anywhere down the centre of the booth it
    // sits on the sight line to the mid hoop, and behind the hoops it is hidden
    // by their own backboards — the staggered layout leaves nowhere else. It
    // goes on the FAR hoop's side and deeper than it is tall, so the near hoop,
    // which fills most of the frame, stays clear.
    // Deep into the booth as well as out to the side: any closer and it fills
    // the edge of the frame at the counter instead of reading as signage.
    const board = this.buildScoreboard({ x: 1.95, y: 2.0, z: 4.6, width: 1.0, height: 0.62 });
    board.rotation.y = 3.55;

    const light = createStallLight(0xffe8c8, 2.2, 12);
    light.position.set(0, 3.2, 3.0);
    this.root.add(light);

    this._offCollision = bus.on(EVENTS.COLLISION, (e) => this._onCollision(e));
  }

  /** @private */
  _buildCourt() {
    const w = HUB.boothWidth - 0.4;
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(w, 0.1, 6.8),
      factory.mat(0xa9793f, { rough: 0.85 }),
    );
    floor.receiveShadow = true;
    this.spawn(floor, { x: 0, y: -0.05, z: 2.8 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: w / 2, y: 0.05, z: 3.4 },
      position: this.localToWorld(0, -0.05, 2.8),
      friction: 0.7, restitution: 0.55,
    });

    const backstop = new THREE.Mesh(
      new THREE.BoxGeometry(w, 3.2, 0.1),
      factory.mat(0x5a4d68, { rough: 0.95 }),
    );
    backstop.receiveShadow = true;
    this.spawn(backstop, { x: 0, y: 1.6, z: 5.6 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: w / 2, y: 1.6, z: 0.05 },
      position: this.localToWorld(0, 1.6, 5.6),
      restitution: 0.3, friction: 0.8,
    });
  }

  /**
   * @param {{key: string, x: number, z: number, points: number, swishOnly: boolean}} def
   * @private
   */
  _buildHoop(def) {
    const local = new THREE.Vector3(def.x, CFG.rimHeight, def.z);
    const group = factory.createHoop(CFG.rimRadius);

    // The hoop group's origin IS the rim centre, so spawning it as the rim's
    // trimesh body puts mesh and collider in the same place for free.
    const rim = this.spawn(group, local, {
      type: 'fixed', shape: 'trimesh', ...rimShell(),
      position: this.localToWorld(local.x, local.y, local.z),
      friction: 0.4, restitution: 0.45,
      userData: { rim: def.key },
    });

    const bb = CFG.backboard;
    const backboard = this.spawn(new THREE.Group(), local, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: bb.width / 2, y: bb.height / 2, z: bb.thickness / 2 },
      position: this.localToWorld(local.x, local.y + bb.y, local.z + bb.z),
      friction: 0.5, restitution: 0.55,
    });

    // A post so the hoop reads as mounted rather than floating. It runs to the
    // top of the backboard — stopped at the rim it leaves a visible gap.
    const post = factory.createPost(local.y + bb.y + bb.height / 2, 0.05);
    post.position.set(local.x, 0, local.z + bb.z + 0.12);
    this.root.add(post);

    // Below the rim, on the post. Above the backboard the label sits outside
    // the frame at the counter and gets cropped away.
    const label = factory.createSign('', {
      w: 0.7, h: 0.24,
      lines: [def.swishOnly ? `${def.points} SWISH` : `${def.points} PTS`],
      bg: def.swishOnly ? '#2a1230' : '#12172a',
    });
    label.position.set(local.x, local.y - 0.45, local.z + bb.z + 0.06);
    label.rotation.y = Math.PI;
    this.root.add(label);

    this.hoops.push({
      key: def.key, def, local,
      world: this.localToWorld(local.x, local.y, local.z),
      group, rim, backboard, throughs: 0, baskets: 0,
    });
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.ballsPerRound });
    this.balls = [];
    this._settleTimer = 0;
    for (const h of this.hoops) {
      h.throughs = 0;
      h.baskets = 0;
      h.rim.userData.scored = false;
      // The hoops are built once, but `_beginRound()` empties the target list
      // immediately before calling this — so they must be re-declared every
      // round or `__cc.stalls.targets()` and `moveTarget()` see nothing.
      this.registerTarget(h.rim, { id: h.key, group: h.key });
    }

    const handles = this.dispense(CFG.ballsPerRound, () => factory.createBall('basketball'), {
      y: 1.31, z: 0.74, spacing: 0.25,
      desc: {
        restitution: CFG.ballRestitution,
        ccd: true,
        events: true,
        angularDamping: 0.04,
        userData: { basketball: true },
      },
    });
    for (const handle of handles) {
      this.balls.push({
        handle, prev: null, rimTouched: new Set(),
        restTimer: 0, age: 0, done: false, resolved: false, result: null,
      });
    }
  }

  // ── Rim contact and spin (GDD 6.4.1) ────────────────────────────────────

  /** @private */
  _onCollision({ a, b, started }) {
    if (!started) return;
    const ball = a?.userData.basketball ? a : (b?.userData.basketball ? b : null);
    if (!ball) return;
    const other = ball === a ? b : a;
    const key = other?.userData.rim;
    if (!key) return;

    const entry = this.balls.find((e) => e.handle === ball);
    if (entry) entry.rimTouched.add(key);
    const hoop = this.hoops.find((h) => h.key === key);
    if (hoop) this._applySpin(ball, hoop);
    audio.play('impact-miss', { volume: 0.45 });
    haptics.play('right', 'impact', { force: 8 });
  }

  /**
   * Nudge the ball by 0.3× the surface velocity its spin produces at the rim
   * contact, in the direction friction would push it. Backspin makes the front
   * of the ball travel upward, so the reaction drags the ball down into the
   * hoop; topspin does the reverse and kicks it out.
   * @private
   */
  _applySpin(handle, hoop) {
    const w = handle.body.angvel();
    const omega = new THREE.Vector3(w.x, w.y, w.z);
    if (omega.lengthSq() < 1e-4) return;

    const pos = handle.position();
    const radial = this._scratch.set(pos.x - hoop.world.x, 0, pos.z - hoop.world.z);
    if (radial.lengthSq() < 1e-8) return;
    radial.normalize();

    // Nearest point on the rim ring, and from there the contact offset from
    // the ball's centre.
    const nearest = new THREE.Vector3(
      hoop.world.x + radial.x * CFG.rimRadius,
      hoop.world.y,
      hoop.world.z + radial.z * CFG.rimRadius,
    );
    const contact = nearest.sub(pos);
    if (contact.lengthSq() < 1e-8) return;
    contact.normalize().multiplyScalar(CFG.ballRadius);

    const surface = new THREE.Vector3().crossVectors(omega, contact);
    handle.setVelocity(handle.velocity().addScaledVector(surface, -CFG.spinInfluence));
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._trackBalls(dt);
    this._checkRoundEnd(dt);
  }

  /** @private */
  _trackBalls(dt) {
    for (const ball of this.balls) {
      if (ball.resolved) continue;
      const h = ball.handle;
      if (h.removed) { ball.resolved = true; continue; }
      if (!h.userData.thrown) continue;

      ball.age += dt;
      const local = this.worldToLocal(h.position());
      if (!ball.done && ball.prev) this._checkRimPlanes(ball, ball.prev, local);
      ball.prev = ball.prev ? ball.prev.copy(local) : local.clone();

      const slow = h.velocity().lengthSq() < 0.25;
      ball.restTimer = slow ? ball.restTimer + dt : 0;
      if (ball.restTimer >= 0.4 || ball.age >= CFG.ballLifetime) {
        ball.resolved = true;
        h.freeze();
      }
    }
  }

  /**
   * Look for a downward crossing of a rim plane inside the rim radius. The
   * crossing point is interpolated between the two samples so a ball moving
   * faster than the rim is thick still registers.
   * @private
   */
  _checkRimPlanes(ball, prev, curr) {
    for (const hoop of this.hoops) {
      const y = hoop.local.y;
      if (prev.y <= y || curr.y > y) continue;      // not a downward crossing

      const t = (prev.y - y) / (prev.y - curr.y);
      const cx = prev.x + (curr.x - prev.x) * t;
      const cz = prev.z + (curr.z - prev.z) * t;
      const dx = cx - hoop.local.x;
      const dz = cz - hoop.local.z;
      if (dx * dx + dz * dz > CFG.rimRadius * CFG.rimRadius) continue;

      hoop.throughs += 1;
      this._onThroughHoop(ball, hoop);
      return;
    }
  }

  /** @private */
  _onThroughHoop(ball, hoop) {
    ball.done = true;
    const rattled = ball.rimTouched.has(hoop.key);

    // GDD 6.4.1: the far hoop pays for a swish only. Rim contact on the way
    // through is worth nothing at all — not a reduced score.
    if (hoop.def.swishOnly && rattled) {
      ball.result = 'rim';
      this.addScore(0, {
        position: hoop.world.clone(),
        label: 'rim — swish only',
        color: '#ff8f8f',
      });
      audio.play('impact-miss');
      this.updateScoreboard();
      return;
    }

    ball.result = rattled ? 'basket' : 'swish';
    hoop.baskets += 1;
    hoop.rim.userData.scored = true;
    this.addScore(hoop.def.points, {
      position: hoop.world.clone(),
      label: hoop.def.swishOnly ? 'SWISH!' : `${hoop.key} hoop`,
      color: hoop.def.swishOnly ? '#ffd700' : '#6fe7c4',
    });
    audio.play('prize-earned', { volume: hoop.def.swishOnly ? 1 : 0.7 });
    haptics.play('right', 'score');
    this.updateScoreboard();
  }

  /** @private */
  _checkRoundEnd(dt) {
    if (!this.roundActive || this.attemptsLeft > 0) return;
    if (this.balls.every((b) => b.resolved)) {
      this.endRound();
      return;
    }
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Aim assist (GDD 5.4: 5° cone) ───────────────────────────────────────

  findAimTarget(releasePos) {
    let best = null;
    let bestD = Infinity;
    for (const hoop of this.hoops) {
      const d = hoop.world.distanceTo(releasePos);
      if (d < bestD) { bestD = d; best = hoop.world; }
    }
    return best ? { position: best.clone() } : null;
  }

  // ── Test hooks ──────────────────────────────────────────────────────────

  /**
   * A hoop is three things — rim body, backboard body, and the visual group —
   * and a fixed body never syncs its mesh. Move them together.
   */
  moveTarget(targetId, localPos, rotation) {
    if (!super.moveTarget(targetId, localPos, rotation)) return false;
    const hoop = this.hoops.find((h) => h.key === targetId);
    if (!hoop) return true;

    hoop.local.set(localPos[0], localPos[1], localPos[2]);
    hoop.group.position.copy(hoop.local);
    hoop.world.copy(this.localToWorld(hoop.local.x, hoop.local.y, hoop.local.z));
    const bb = CFG.backboard;
    hoop.backboard.setPosition(
      this.localToWorld(hoop.local.x, hoop.local.y + bb.y, hoop.local.z + bb.z),
    );
    return true;
  }

  /** Diagnostic state for `__cc.stalls.debug()`. */
  debugState() {
    return {
      stallId: this.id,
      hoops: this.hoops.map((h) => ({
        key: h.key, points: h.def.points, swishOnly: h.def.swishOnly,
        throughs: h.throughs, baskets: h.baskets, local: h.local.toArray(),
      })),
      balls: this.balls.map((b) => ({
        thrown: !!b.handle.userData.thrown,
        rimTouched: [...b.rimTouched],
        result: b.result,
        resolved: b.resolved,
      })),
    };
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const made = this.hoops.reduce((n, h) => n + h.baskets, 0);
    const far = this.hoops.find((h) => h.key === 'far');
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Balls   ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Baskets ${made} / ${this.maxAttempts}`,
      `Swish   ${far ? far.baskets : 0}`,
    ]);
  }

  onTeardown() {
    if (this._offCollision) this._offCollision();
    this._offCollision = null;
    this.hoops = [];
    this.balls = [];
  }
}

registerStall({
  id: 'basketball-shoot',
  name: 'Basketball Shoot',
  cost: CFG.cost,
  create: (ctx) => new BasketballShoot(ctx),
});

export { BasketballShoot };
